import { ChevronRight } from "lucide-react";
import * as React from "react";
import { Badge, type BadgeVariant } from "../atoms/badge.js";
import { cx } from "../internal/cx.js";

export type ListItemCardLayout = "card" | "row";
export type ListItemCardBodyChrome = "default" | "none";
export type ListItemCardLeadingVariant = "icon" | "avatar" | "none";
export type ListItemCardStatusTone = "neutral" | "success" | "warning" | "danger";

export interface ListItemCardStatus {
  label: React.ReactNode;
  tone?: ListItemCardStatusTone;
}

export interface ListItemCardProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "title" | "children" | "onClick" | "disabled"> {
  /** Leading slot — icon, avatar, status indicator. */
  leading?: React.ReactNode;
  /**
   * How to chrome the leading slot.
   *  - 'icon' (default): renders the leading inside a gray-medallion square,
   *    sized for a 20px lucide icon.
   *  - 'avatar': no chrome, used for Avatar or other self-sized media.
   *  - 'none': no wrapper at all.
   */
  leadingVariant?: ListItemCardLeadingVariant;
  /** Primary text — should be a noun phrase or actionable label. */
  title: React.ReactNode;
  /** Optional supporting text. */
  description?: React.ReactNode;
  /** Optional column shown to the right of the title (e.g. amount). */
  metadata?: React.ReactNode;
  /** Status pill — replaces (or precedes) the trailing slot when present. */
  status?: ListItemCardStatus;
  /** Trailing slot — action button, badge, or value. */
  trailing?: React.ReactNode;
  /** Optional nested body content. */
  children?: React.ReactNode;
  /** Visual chrome for nested body content. Default includes divider + spacing. */
  bodyChrome?: ListItemCardBodyChrome;
  /** Click handler — when present (and not `disabled`), the row becomes a button. */
  onClick?: () => void;
  /** Visually emphasize the row. */
  selected?: boolean;
  /** Hide the chevron even when the row is interactive. */
  hideChevron?: boolean;
  /** When the row toggles expandable content; sets `aria-expanded` on the button. */
  ariaExpanded?: boolean;
  /** Disabled native button without an action (still participates in list semantics). */
  disabled?: boolean;
  /** Visual chrome. Default 'card'. */
  layout?: ListItemCardLayout;
}

function statusToneToBadgeVariant(tone: ListItemCardStatusTone | undefined): BadgeVariant {
  if (tone === "success") return "success";
  if (tone === "warning") return "warning";
  if (tone === "danger") return "danger";
  return "neutral";
}

/**
 * Single row card for vertical lists. Covers the "row with a left slot +
 * primary text + secondary text + right-side value / action" pattern. When
 * `onClick` is set, the entire row becomes a native button with hover and
 * keyboard support; nested status / trailing / metadata stop propagation so
 * inner menus don't trigger the row.
 */
export const ListItemCard = React.forwardRef<HTMLButtonElement | HTMLDivElement, ListItemCardProps>(
  function ListItemCard(
    {
      ariaExpanded,
      bodyChrome = "default",
      children,
      className,
      description,
      disabled = false,
      hideChevron,
      layout = "card",
      leading,
      leadingVariant = "icon",
      metadata,
      onClick,
      selected,
      status,
      title,
      trailing,
      ...props
    },
    ref,
  ) {
    const interactive = Boolean(onClick) && !disabled;
    const renderAsButton = interactive || (!onClick && disabled);
    const containerClassName = cx(
      "cw-v2-list-item-card",
      `cw-v2-list-item-card--${layout}`,
      interactive && "cw-v2-list-item-card--interactive",
      !onClick && disabled && "cw-v2-list-item-card--disabled",
      selected && "cw-v2-list-item-card--selected",
      className,
    );

    const body = (
      <>
        <div className="cw-v2-list-item-card__row">
          {leading && leadingVariant !== "none" ? (
            <div
              className={cx(
                "cw-v2-list-item-card__leading",
                `cw-v2-list-item-card__leading--${leadingVariant}`,
              )}
            >
              {leading}
            </div>
          ) : leading && leadingVariant === "none" ? (
            leading
          ) : null}
          <div className="cw-v2-list-item-card__copy">
            <div className="cw-v2-list-item-card__title">{title}</div>
            {description ? (
              <div className="cw-v2-list-item-card__description">{description}</div>
            ) : null}
          </div>
          {metadata ? (
            // biome-ignore lint/a11y/useKeyWithClickEvents: this div is not a control — onClick only stops propagation so clicks on metadata don't trigger the outer card; child controls retain their own keyboard handlers.
            <div
              className="cw-v2-list-item-card__metadata"
              onClick={(e) => interactive && e.stopPropagation()}
            >
              {metadata}
            </div>
          ) : null}
          {status ? (
            // biome-ignore lint/a11y/useKeyWithClickEvents: this div is not a control — onClick only stops propagation so clicks on the status badge don't trigger the outer card; child controls retain their own keyboard handlers.
            <div
              className="cw-v2-list-item-card__status"
              onClick={(e) => interactive && e.stopPropagation()}
            >
              <Badge size="sm" variant={statusToneToBadgeVariant(status.tone)}>
                {status.label}
              </Badge>
            </div>
          ) : null}
          {trailing ? (
            // biome-ignore lint/a11y/useKeyWithClickEvents: this div is not a control — onClick only stops propagation so clicks on the trailing slot don't trigger the outer card; child controls retain their own keyboard handlers.
            <div
              className="cw-v2-list-item-card__trailing"
              onClick={(e) => interactive && e.stopPropagation()}
            >
              {trailing}
            </div>
          ) : null}
          {interactive && !hideChevron ? (
            <ChevronRight aria-hidden className="cw-v2-list-item-card__chevron" />
          ) : null}
        </div>
        {children ? (
          <div
            className={cx(
              "cw-v2-list-item-card__body",
              bodyChrome === "none" && "cw-v2-list-item-card__body--no-chrome",
            )}
          >
            {children}
          </div>
        ) : null}
      </>
    );

    const ariaExpandedProps = ariaExpanded !== undefined ? { "aria-expanded": ariaExpanded } : {};

    if (renderAsButton) {
      return (
        <button
          ref={ref as React.Ref<HTMLButtonElement>}
          className={containerClassName}
          {...props}
          type="button"
          {...ariaExpandedProps}
          disabled={!onClick && disabled}
          onClick={onClick}
        >
          {body}
        </button>
      );
    }

    return (
      <div ref={ref as React.Ref<HTMLDivElement>} className={containerClassName} {...props}>
        {body}
      </div>
    );
  },
);
ListItemCard.displayName = "ListItemCard";
